/**
 * localPrefs — guarded, per-viewer UI preferences in localStorage.
 *
 * One home for the "remember what this viewer chose" pattern the calendar's
 * view mode introduced (`nurturecrm.calendar.viewMode`, #540 era): a read that
 * validates what it finds and falls back, and a write that never throws.
 * Storage throws in some privacy modes and can hold a value an older build
 * wrote (a calendar view that no longer exists), so neither side may trust it.
 *
 * Preferences only. Nothing here may hold state another viewer, another
 * device or the server needs to see.
 */

/**
 * @template T
 * @param {string} key
 * @param {T} fallback - returned when the key is absent, unreadable or invalid
 * @param {(value: string) => boolean} [isValid] - reject values an older build stored
 * @returns {string|T}
 */
export function readPref(key, fallback, isValid) {
    try {
        const stored = localStorage.getItem(key);
        if (stored === null) return fallback;
        if (isValid && !isValid(stored)) return fallback;
        return stored;
    } catch {
        return fallback;
    }
}

/**
 * @param {string} key
 * @param {string|null} value - null removes the key
 */
export function writePref(key, value) {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch {
        // Private mode / quota — the preference simply isn't remembered.
    }
}

/** JSON variant of readPref, for small structured preferences. */
export function readJsonPref(key, fallback, isValid) {
    const raw = readPref(key, null);
    if (raw === null) return fallback;
    try {
        const parsed = JSON.parse(raw);
        return !isValid || isValid(parsed) ? parsed : fallback;
    } catch {
        return fallback;
    }
}

export function writeJsonPref(key, value) {
    writePref(key, value === null ? null : JSON.stringify(value));
}
